/**
 * The ONE place the product formats a date or a time.
 *
 * WHY IT LIVES IN `lib/` AND NOT IN A FEATURE. It started as `features/sessions/sessionDateTime.ts`
 * because the session views were the first screens that needed it. The questionnaires list needs
 * exactly the same "14 Aug 2026, 12:21 pm", and a feature may not import from another feature
 * (`.claude/rules/frontend/features.md`) — so the choice moves here, where anything can reach it,
 * rather than being copied and left to drift.
 *
 * Before it existed, every screen did its own. Nine step Detail components and the return-data
 * catalog each called a bare `d.toLocaleString()`, which takes whatever the browser's locale
 * happens to be — so "Verified At" rendered as `18/08/2026, 11:10:14`: 24-hour, seconds included,
 * day/month order that flips between visitors, and a format nobody chose. The session Overview
 * meanwhile showed `14 Aug 2026` with no time at all, so one page reported the same timestamp two
 * different ways.
 *
 * Fixed here so the choice is made once:
 *
 *   • `en-IN` — this is an Indian KYC product; a US or UK browser must not reorder the date
 *   • 12-hour with am/pm, because that is how times are read back to people here
 *   • NO seconds — nobody reconciles a KYC record to the second, and they only add noise
 *   • `d MMM yyyy` (14 Aug 2026), never numeric, so 14/08 can never be misread as 8 Feb
 */

const DATE_ONLY = new Intl.DateTimeFormat('en-IN', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const DATE_AND_TIME = new Intl.DateTimeFormat('en-IN', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const TIME_ONLY = new Intl.DateTimeFormat('en-IN', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

/**
 * With seconds — "06:16:20 pm".
 *
 * The ONE place seconds are wanted, and only in a list's second line. Elsewhere they are noise (see
 * the note above), but a list of records created seconds apart — several questionnaires drafted in
 * one sitting — is exactly where the minute alone stops telling you which came first. `2-digit`
 * hour here rather than `numeric`, so the column's values are all the same width and the times line
 * up down the page.
 */
const TIME_WITH_SECONDS = new Intl.DateTimeFormat('en-IN', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
});

/** Parses an ISO string, or null when it is absent or unparseable. */
function parse(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Date only — "14 Aug 2026".
 *
 * For a value that genuinely has no time to show (a date of birth, a document's issue date). A
 * timestamp should use {@link formatDateTime} instead.
 */
export function formatDate(value: string | null | undefined, fallback = 'NA'): string {
  const d = parse(value);
  return d ? DATE_ONLY.format(d) : fallback;
}

/**
 * Date and time — "14 Aug 2026, 12:21 pm".
 *
 * The default for anything that happened at a moment: created, submitted, verified, decided. In
 * the viewer's own timezone, which is the right choice for an admin reading their own queue.
 */
export function formatDateTime(value: string | null | undefined, fallback = 'NA'): string {
  const d = parse(value);
  return d ? DATE_AND_TIME.format(d) : fallback;
}

/**
 * Time only — "12:21 pm".
 *
 * For a table that stacks the date and the time on two lines (the questionnaires and sessions
 * lists), where repeating the date on the second line would just be noise.
 */
export function formatTime(value: string | null | undefined, fallback = ''): string {
  const d = parse(value);
  return d ? TIME_ONLY.format(d) : fallback;
}

/** Time with seconds — "06:16:20 pm". For a list's stacked date/time cell. */
export function formatTimeWithSeconds(value: string | null | undefined, fallback = ''): string {
  const d = parse(value);
  return d ? TIME_WITH_SECONDS.format(d) : fallback;
}

/**
 * A value out of a stored verification result, which may be a full timestamp or a bare date.
 * Returns the input unchanged when it is not a date at all, because a provider can put anything in
 * a field the catalog has typed as a date and showing the raw value beats showing "NA".
 *
 * A date-only string ("1994-02-20") is formatted WITHOUT a time. Formatting it with one would
 * invent `12:00 am` and present midnight as if the provider had reported it.
 */
export function formatResultDateTime(value: unknown): string {
  const raw = String(value);
  const d = parse(raw);
  if (!d) return raw;
  return isDateOnly(raw) ? DATE_ONLY.format(d) : DATE_AND_TIME.format(d);
}

/** True for `YYYY-MM-DD` / `DD-MM-YYYY` / `DD/MM/YYYY` and friends — a date with no clock. */
function isDateOnly(raw: string): boolean {
  return !/[T ]\d{1,2}:\d{2}/.test(raw.trim());
}
